import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEMO_SCRAPE_WAIT_SECONDS } from '../../../lib/demo/waitForDemoScrape';

const SCRAPE_STEP_COUNT = 7;

const DemoScrapeOverlay = ({ durationSeconds = DEMO_SCRAPE_WAIT_SECONDS }) => {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(durationSeconds);

  useEffect(() => {
    const startedAt = Date.now();
    setRemaining(durationSeconds);

    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setRemaining(Math.max(0, durationSeconds - elapsed));
    };

    const timer = setInterval(tick, 200);
    return () => clearInterval(timer);
  }, [durationSeconds]);

  const elapsed = Math.max(0, durationSeconds - remaining);
  const stepIndex = Math.min(
    SCRAPE_STEP_COUNT - 1,
    Math.floor((elapsed / durationSeconds) * SCRAPE_STEP_COUNT),
  );
  const steps = t('demo.scrapingSteps', { returnObjects: true });
  const stepLabel = Array.isArray(steps) ? steps[stepIndex] : t('demo.scrapingOverlayBody');

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/85 backdrop-blur-md lg:left-[240px]"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-valuemin={0}
      aria-valuemax={durationSeconds}
      aria-valuenow={remaining}
    >
      <div className="flex flex-col items-center px-6 text-center max-w-md">
        <div className="demo-scrape-stage" aria-hidden="true">
          <span className="demo-scrape-ring" />
          <span className="demo-scrape-ring demo-scrape-ring-delay" />
          <div className="demo-scrape-card">
            <span className="demo-scrape-scan" />
            <div className="demo-scrape-avatar" />
            <div className="demo-scrape-lines">
              <span className="w-[72%]" />
              <span className="w-[48%]" />
              <span className="w-full" />
              <span className="w-[86%]" />
              <span className="w-[62%]" />
            </div>
          </div>
        </div>
        <h3 className="mt-8 text-xl md:text-2xl font-semibold text-foreground">
          {t('demo.scrapingOverlayTitle')}
        </h3>
        <p
          className="mt-4 text-5xl md:text-6xl font-semibold tabular-nums tracking-tight text-primary"
          aria-label={t('demo.scrapingOverlayCountdown', { count: remaining })}
        >
          {remaining}
          <span className="ml-1 text-2xl md:text-3xl font-medium text-primary/70">s</span>
        </p>
        <div key={stepIndex} className="demo-scrape-step mt-4 flex items-center justify-center gap-2.5 min-h-12">
          <span className="demo-scrape-step-dot" aria-hidden="true" />
          <p className="text-sm md:text-base font-medium text-foreground leading-relaxed">
            {stepLabel}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DemoScrapeOverlay;
